import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PlusCircle, Save, Trash2, Power, ChevronRight, Package } from "lucide-react";
import { Modal } from "@/pages/Customers";

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [subsByCat, setSubsByCat] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [catModal, setCatModal] = useState(null);
  const [subModal, setSubModal] = useState(null);

  async function refresh() {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
      const subs = await api.get("/subcategories");
      const map = {};
      for (const s of subs.data) {
        if (!map[s.category_id]) map[s.category_id] = [];
        map[s.category_id].push(s);
      }
      setSubsByCat(map);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => { refresh(); }, []);

  async function saveCat() {
    try {
      if (catModal.id) {
        await api.patch(`/categories/${catModal.id}`, { name: catModal.name, icon: catModal.icon });
        toast.success("Categoría actualizada");
      } else {
        await api.post("/categories", { name: catModal.name, icon: catModal.icon || "Package" });
        toast.success("Categoría creada");
      }
      setCatModal(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function saveSub() {
    try {
      if (subModal.id) {
        await api.patch(`/subcategories/${subModal.id}`, { name: subModal.name });
        toast.success("Subcategoría actualizada");
      } else {
        await api.post("/subcategories", { category_id: subModal.category_id, name: subModal.name });
        toast.success("Subcategoría creada");
      }
      setSubModal(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function toggleCat(c) {
    try { await api.patch(`/categories/${c.id}`, { active: !c.active }); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function delCat(c) {
    if (!confirm(`¿Eliminar categoría ${c.name}? También sus subcategorías.`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Eliminada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function toggleSub(s) {
    try { await api.patch(`/subcategories/${s.id}`, { active: !s.active }); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function delSub(s) {
    if (!confirm(`¿Eliminar ${s.name}?`)) return;
    try { await api.delete(`/subcategories/${s.id}`); toast.success("Eliminada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Categorías</h1>
          <p className="text-slate-500 text-sm mt-1">Estructura de categorías y subcategorías/modelos.</p>
        </div>
        <button onClick={() => setCatModal({ name: "", icon: "Package" })} className="btn-primary" data-testid="categories-new-button">
          <PlusCircle size={18} /> Nueva categoría
        </button>
      </div>

      <div className="space-y-2">
        {categories.map((c) => {
          const open = expanded === c.id;
          const subs = subsByCat[c.id] || [];
          return (
            <div key={c.id} className={`card-base p-0 overflow-hidden ${!c.active ? "opacity-60" : ""}`} data-testid={`category-row-${c.name}`}>
              <div className="flex items-center p-4 gap-3">
                <button onClick={() => setExpanded(open ? null : c.id)} className="btn-ghost" data-testid={`category-expand-${c.name}`}>
                  <ChevronRight size={18} className={`transition-transform ${open ? "rotate-90" : ""}`} />
                </button>
                <Package size={18} className="text-blue-600" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold">{c.name}</div>
                  <div className="text-xs text-slate-500">{subs.length} subcategorías</div>
                </div>
                <div className={`text-[10px] font-mono uppercase ${c.active ? "text-green-600" : "text-red-500"}`}>
                  {c.active ? "Activa" : "Inactiva"}
                </div>
                <button onClick={() => toggleCat(c)} className="btn-ghost" data-testid={`category-toggle-${c.name}`}><Power size={16} /></button>
                <button onClick={() => setCatModal({ ...c })} className="btn-ghost text-xs" data-testid={`category-edit-${c.name}`}>Editar</button>
                <button onClick={() => delCat(c)} className="btn-ghost text-red-500" data-testid={`category-delete-${c.name}`}><Trash2 size={16} /></button>
              </div>
              {open && (
                <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-2">
                  {subs.map((s) => (
                    <div key={s.id} className={`flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 ${!s.active ? "opacity-60" : ""}`} data-testid={`subcategory-row-${s.name}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{s.name}</div>
                      </div>
                      <div className={`text-[10px] font-mono uppercase ${s.active ? "text-green-600" : "text-red-500"}`}>
                        {s.active ? "act" : "off"}
                      </div>
                      <button onClick={() => toggleSub(s)} className="btn-ghost" data-testid={`subcategory-toggle-${s.name}`}><Power size={14} /></button>
                      <button onClick={() => setSubModal({ ...s })} className="btn-ghost text-xs">Editar</button>
                      <button onClick={() => delSub(s)} className="btn-ghost text-red-500"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button
                    onClick={() => setSubModal({ category_id: c.id, name: "" })}
                    className="btn-secondary w-full"
                    data-testid={`add-subcategory-${c.name}`}
                  >
                    <PlusCircle size={14} /> Agregar subcategoría
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {catModal && (
        <Modal onClose={() => setCatModal(null)} title={catModal.id ? "Editar categoría" : "Nueva categoría"}>
          <div className="space-y-3">
            <input value={catModal.name} onChange={(e) => setCatModal({ ...catModal, name: e.target.value })} placeholder="Nombre" className="input-base" data-testid="cat-modal-name" />
            <input value={catModal.icon || ""} onChange={(e) => setCatModal({ ...catModal, icon: e.target.value })} placeholder="Icono (lucide name)" className="input-base" data-testid="cat-modal-icon" />
            <button onClick={saveCat} className="btn-primary w-full" data-testid="cat-modal-save"><Save size={16} /> Guardar</button>
          </div>
        </Modal>
      )}
      {subModal && (
        <Modal onClose={() => setSubModal(null)} title={subModal.id ? "Editar subcategoría" : "Nueva subcategoría"}>
          <div className="space-y-3">
            <input value={subModal.name} onChange={(e) => setSubModal({ ...subModal, name: e.target.value })} placeholder="Nombre" className="input-base" data-testid="sub-modal-name" />
            <button onClick={saveSub} className="btn-primary w-full" data-testid="sub-modal-save"><Save size={16} /> Guardar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
